import React, { useState, useEffect, Fragment, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Grid, Divider, Box, makeStyles, Typography, Tooltip } from '@material-ui/core';
import { useRouteMatch } from 'react-router';

import { useProjectApi /*, useMonitoringPlanApi */ } from 'src/services';
import { AnalyticCard } from 'src/scenes/Project/components';
import { EcosystemInventoryTable, EcosystemTrendsChart } from 'src/scenes/Project/components';
import { ConcernSummary, ProtectedAndPriorityZones,
  LoadingBox, NoProcessBox, GeospatialAnalytic } from 'src/scenes/Project/scenes/Dashboard/components';
import { SpeciesTypeFilter, EeccList, EeccCondensed } from 'src/scenes/Project/components';
import { useFetchStatus } from 'src/hooks';
import { ContentContainer, DialogWrap, TitleContainer, DialogExternalImage, FilterYearComparison, Chip } from 'src/components';
import { getLeafletOptionsFn } from 'src/scenes/Project/scenes/Analytics/smaAnalyticsHelper';
import { WebContext } from 'src/scenes/web-context';
import { getEiiDetailOptions } from 'src/utils/geospatialHelpers';
import { processComponentConcerns, componentConfig } from 'src/utils/natureStateIndicatorsHelper';


const useStyles = makeStyles(theme => ({
  divider: {
    width: '100%',
    marginBottom: theme.spacing(1),
  },
  indicatorLabel: {
    fontStyle: 'italic',
    display: 'inline',
  },
  group: {
    padding: '3px',
    borderRadius: theme.shape.borderRadius,
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: theme.palette.common.gray400,
  },
  comparisonYearGroup: {
    border: `1px solid ${theme.palette.common.gray}`,
    borderRadius: theme.shape.borderRadius,
  },
}));


const eiiMainTypes = [ 'eii', 'eii-reclass' ];

const BiodiversityContainer = ({ interestArea, project }) => {
  const match = useRouteMatch();
  const classes = useStyles();
  const { currentUser: { token } } = useContext(WebContext);

  const projectApi = useProjectApi();
  const componentId = match.url.split('/').filter(Boolean).pop();

  let { projectId, siteId } = match.params;
  const isSite = Boolean(siteId);
  projectId = isSite ? siteId : projectId;

  const [ concerns, setConcerns ] = useState([]);
  const [ geospatialAnalytics, setGeospatialAnalytics ] = useState([]);
  const [ eeccTypeFilter, setEeccTypeFilter ] = useState('flora');
  const [ eeccCondensedTypeFilter, setEeccCondensedTypeFilter ] = useState('total');
  const [ ecosystemComparisonYears, setEcosystemComparisonYears ] = useState([ 2020, 2024 ]);
  const [ conditionChartViewMode, setConditionChartViewMode ] = useState('vegetation-floor');
  const [ selectedEiiDetail, setSelectedEiiDetail ] = useState({ label: 'Composición', value: 'composition' });
  const [ geospatialOptions, setGeospatialOptions ] = useState({
    eiiMainOptions: [],
    eiiDetailOptions: [],
  });

  const eiiDetailOptions = useMemo(() =>
    getEiiDetailOptions(geospatialOptions.eiiDetailOptions), [ geospatialOptions ]);
  // Opciones para el filtro del gráfico de condición
  const chartViewModeOptions = [
    { label: 'Formación', value: 'formation' },
    { label: 'Piso vegetacional', value: 'vegetation-floor' },
    { label: 'Uso de suelo', value: 'land-use' },
  ];

  // Columnas a mostrar en la tabla simplificada (dinámicas según los años)
  const ecosystemTableColumns = useMemo(() => [
    'ecosystem', // Piso vegetacional
    'conservationStatus', // Estado de conservación
    `area${ecosystemComparisonYears[0]}`, // Área año 1
    `condition${ecosystemComparisonYears[0]}`, // Condición año 1
    `area${ecosystemComparisonYears[1]}`, // Área año 2
    `condition${ecosystemComparisonYears[1]}`, // Condición año 2
    'performance',
  ], [ ecosystemComparisonYears ]);

  const handleEcosystemYearUpdate = newYears => {
    setEcosystemComparisonYears(newYears);
  };

  const handleConditionChartViewModeChange = newViewMode => {
    setConditionChartViewMode(newViewMode);
  };

  const eiiDetailConfig = {
    options: eiiDetailOptions,
    selected: selectedEiiDetail,
    onChange: setSelectedEiiDetail,
  };

  // Opciones geoespaciales actuales para EII Detail basadas en la selección
  const currentEiiDetailGeospatialOptions = useMemo(() => {
    if (!geospatialOptions?.eiiDetailOptions) {
      return [];
    }
    return geospatialOptions?.eiiDetailOptions[selectedEiiDetail?.value] || [];
  }, [ selectedEiiDetail?.value, geospatialOptions ]);

  // const [ selectedMonitoringPlan, setSelectedMonitoringPlan ] = useState();
  // const [ monitoringPlanOptions, setMonitoringPlanOptions ] = useState([]);

  const [ eeccs, setEeccs ] = useState({});
  const [ protectedAndPriorityZones, setProtectedAndPriorityZones ] = useState([]);
  const [ ecosystems, setEcosystems ] = useState();

  const [ dialog, setDialog ] = useState({ isOpen: false, data: {}, type: '' });
  const closeDialog = () => setDialog({ isOpen: false, type: '' });
  const toggleDialog = ({ data = {}, type = '' } = {}) => setDialog({ isOpen: !dialog.isOpen, data, type });

  const getDialogType = () => {
    switch (dialog.type) {
      case 'photo':
        return <DialogExternalImage
          pictureData={dialog.data.pictureData}
          additionalClass='image-species-container'
        />;
      default:
        break;
    }
  };

  const {
    handleConfirm: fetchEcosystems,
    isSuccess: isSuccessEcosystems,
    isLoading: isLoadingEcosystems } = useFetchStatus({
    confirmFn: async () => {
      const ecosystems = await projectApi.getEcosystems({ projectId });
      if (ecosystems?.availableYears?.length) {
        const availableYears = ecosystems.availableYears;
        const firstYear = availableYears[0];
        const secondYear = availableYears.length > 1 ? ecosystems.availableYears[1] : firstYear;
        setEcosystemComparisonYears([ firstYear, secondYear ]);
      }
      setEcosystems(ecosystems);
    },
  });

  useEffect(() => {
    fetchEcosystems();
  }, []); //eslint-disable-line

  const { handleConfirm: fetchConcerns, isLoading: isLoadingConcerns, isSuccess: isSuccessConcerns } = useFetchStatus({
    confirmFn: async () => {
      const indicators = await projectApi.getNatureStateIndicators({ projectId });

      const processedConcerns = processComponentConcerns(indicators, 'biodiversity', 2024);

      const concerns = processedConcerns.map(concern => {
        const indicatorConfig = componentConfig.biodiversity.indicators[concern.fieldKey];
        return {
          ...concern,
          tooltip: indicatorConfig.tooltip,
          url: indicatorConfig.url,
          ReasonComponent: () =>
            <>
              basado en el
              <Typography className={classes.indicatorLabel} variant="body2" component="span">
                &nbsp;{indicatorConfig.description}&nbsp;
              </Typography>
              del indicador
              <Typography className={classes.indicatorLabel} variant="body2" component="span">
                &nbsp;{indicatorConfig.fullName} ({indicatorConfig.shortName})
              </Typography>
            </>,
        };
      });

      setConcerns(concerns);
    },
  });

  useEffect(() => {
    fetchConcerns();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { isSuccess: isSuccessEecc, isLoading: isLoadingEecc, handleConfirm: fetchEecc } = useFetchStatus({
    confirmFn: async () => {
      const eeccOccurrences = await projectApi.getEeccOccurrences({ projectId });
      setEeccs(eeccOccurrences);
    },
  });

  useEffect(() => {
    // if (selectedMonitoringPlan?.value) {
    fetchEecc();
    // }
  }, [ /* selectedMonitoringPlan?.value */ ]); // eslint-disable-line react-hooks/exhaustive-deps

  const {
    isSuccess: isSuccessProtectedAndPriorityZones,
    isLoading: isLoadingProtectedAndPriorityZones,
    handleConfirm: fetchProtectedAndPriorityZones,
  } = useFetchStatus({
    confirmFn: async () => {
      const protectedAndPriorityZones = await projectApi.getProtectedAndPriorityZones(projectId);
      setProtectedAndPriorityZones(protectedAndPriorityZones);
    },
  });

  useEffect(() => {
    fetchProtectedAndPriorityZones();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const {
    handleConfirm: fetchGeospatialAnalytics,
    isLoading: isLoadingGeospatialAnalytics,
    isSuccess: isSuccessGeospatialAnalytics,
  } = useFetchStatus({
    confirmFn: async () => {
      const [ geospatialAnalytics, geospatialOptions ] = await Promise.all([
        projectApi.getGeospatialAnalytics({ projectId, componentId }),
        projectApi.getGeospatialOptions({ projectId, componentId }),
      ]);
      setGeospatialOptions(geospatialOptions);
      setGeospatialAnalytics(geospatialAnalytics);
    },
  });

  useEffect(() => {
    fetchGeospatialAnalytics();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const hasEecc = eeccs?.flora?.length > 0 || eeccs?.fauna?.length > 0;
  const hasEcosystems = ecosystems?.ecosystems?.length > 0;
  const hasProtectedZones = protectedAndPriorityZones.length > 0;

  const eiiAnalytic = useMemo(() => geospatialAnalytics.find(g => eiiMainTypes.includes(g.geoInfoTypeId)), [ geospatialAnalytics ]);
  const detailAnalytic = useMemo(() => geospatialAnalytics.find(g => !eiiMainTypes.includes(g.geoInfoTypeId)), [ geospatialAnalytics ]);

  return (
    <Grid container>
      <TitleContainer
        maxWidth='xl'
        project={project}
        breadcrumbs={[ 'projects', 'project', 'sites', 'site', { name: 'Biodiversidad' } ]}
      >
        Estado de la naturaleza - Biodiversidad
      </TitleContainer>
      <ContentContainer maxWidth="xl">
        <Grid container spacing={1}>
          <Grid item xs={12}>
            <AnalyticCard title="Indicadores">
              <Divider />
              <Box my={0.5} />
              {isLoadingConcerns && <LoadingBox label="indicadores de biodiversidad" />}
              {isSuccessConcerns &&
                <Box display="flex" flexDirection="column" alignItems="center">
                  {concerns.map(({ name, level, value, isPartial, tooltip, url, ReasonComponent }, index) =>
                    <Fragment key={`concern-${index}`}>
                      <ConcernSummary title={name} level={level} value={value} isPartial={isPartial} tooltip={tooltip} url={url}>
                        Nivel de preocupación <strong>{level.name}</strong> para {name} <ReasonComponent />
                      </ConcernSummary>
                      {(concerns.length - 1) !== index && <Divider className={classes.divider} />}
                    </Fragment>,
                  )}
                </Box>
              }
            </AnalyticCard>
          </Grid>
        </Grid>
        <Box my={0.5} />
        <Grid container spacing={1}>
          <GeospatialAnalytic
            title="Integridad de los ecosistemas"
            analytic={eiiAnalytic}
            interestArea={interestArea.data}
            getLeafletOptions={bounds => getLeafletOptionsFn({ token, bounds })}
            geospatialTypesOptions={geospatialOptions.eiiMainOptions}
            fetchStatus={{ isLoading: isLoadingGeospatialAnalytics, isSuccess: isSuccessGeospatialAnalytics }}
          />
        </Grid>

        <Box my={0.5} />
        {(hasEcosystems || isLoadingEcosystems) &&
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <AnalyticCard
                title="Ecosistemas"
                ActionComponent={() => hasEcosystems ?
                  <Box display="flex" alignItems="center">
                    <Box className={classes.comparisonYearGroup} height={34}>
                      <FilterYearComparison
                        id="ecosystem-year-comparison"
                        prevValue={ecosystemComparisonYears}
                        options={ecosystems.availableYears}
                        onChange={handleEcosystemYearUpdate}
                      />
                    </Box>
                  </Box> : null
                }
              >
                <Divider />
                <Box mt={2}>
                  {isLoadingEcosystems && <LoadingBox label="Ecosistemas" />}
                  {isSuccessEcosystems && hasEcosystems && <EcosystemInventoryTable
                    ecosystems={ecosystems}
                    comparisonYears={ecosystemComparisonYears}
                    viewMode="vegetation-floor"
                    areaType="area"
                    visibleColumns={ecosystemTableColumns}
                  />}
                </Box>
              </AnalyticCard>
            </Grid>
          </Grid>
        }

        <Box my={0.5} />
        {(isLoadingEcosystems || hasEcosystems) &&
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <AnalyticCard
                title="Evolución de la condición por ecosistema"
                ActionComponent={() =>
                  <Tooltip title="Agrupar datos del gráfico" placement="top">
                    <Box display="flex" className={classes.group} p={0.5}>
                      {chartViewModeOptions.map((option, index) => (
                        <Fragment key={option.value}>
                          <Chip
                            color={conditionChartViewMode === option.value ? 'secondary.main' : 'gray'}
                            label={option.label}
                            size="small"
                            onClick={() => handleConditionChartViewModeChange(option.value)}
                            width={120}
                          />
                          {index < chartViewModeOptions.length - 1 && <Box mx={0.5} />}
                        </Fragment>
                      ))}
                    </Box>
                  </Tooltip>
                }
              >
                <Divider />
                <Box mt={2}>
                  {isLoadingEcosystems && <LoadingBox label="gráfico de ecosistemas" />}
                  {isSuccessEcosystems && hasEcosystems && <EcosystemTrendsChart
                    ecosystems={ecosystems}
                    viewMode={conditionChartViewMode}
                    metric="condition"
                  />}
                </Box>
              </AnalyticCard>
            </Grid>
          </Grid>
        }
        <Box my={0.5} />
        <Grid container spacing={1}>
          <GeospatialAnalytic
            id="eii-detail"
            title="Detalle de integridad de los ecosistemas"
            analytic={detailAnalytic}
            interestArea={interestArea.data}
            getLeafletOptions={bounds => getLeafletOptionsFn({ token, bounds })}
            eiiDetailConfig={eiiDetailConfig}
            geospatialTypesOptions={currentEiiDetailGeospatialOptions}
            fetchStatus={{ isLoading: isLoadingGeospatialAnalytics, isSuccess: isSuccessGeospatialAnalytics }}
          />
        </Grid>

        <Box my={0.5} />

        {(hasEecc || isLoadingEecc) &&
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <AnalyticCard
                title="Lista de EECC"
                ActionComponent={
                  () =>
                    <>
                      {/* <Box width={200}>
                        <AutocompleteWrapper
                          placeholder="Plan de monitoreo"
                          value={{ label: selectedMonitoringPlan?.label, value: selectedMonitoringPlan?.value } || { label: '', value: '' }}
                          options={monitoringPlanOptions}
                          onChange={(_, option) => setSelectedMonitoringPlan(option)}
                          size="very-small"
                          getOptionSelected={(option, value) => (option.value === value.value || value.value === '')}
                          clearable
                          fullWidth
                          fixEmptyWarn
                        />
                      </Box> */}
                      <Box display="flex" className={classes.group} p={0.5} ml={1}>
                        <SpeciesTypeFilter speciesType={eeccTypeFilter} setSpeciesType={setEeccTypeFilter} showAllOption={false} />
                      </Box>
                    </>
                }
              >
                <Divider />
                <Box my={0.5} />
                {isLoadingEecc && <LoadingBox label="Lista de EECC" />}
                {(isSuccessEecc && !hasEecc) &&
                  <NoProcessBox label='No se encontraron especies EECC' />
                }
                {(hasEecc && isSuccessEecc) &&
                  <EeccList
                    eeccs={eeccs}
                    eeccTypeFilter={eeccTypeFilter}
                    actions={{ toggleDialog }}
                  />
                }
              </AnalyticCard>
            </Grid>
            <Grid item xs={6}>
              <AnalyticCard
                title="EECC agrupados"
                ActionComponent={
                  () =>
                    <>
                      {/* <Box width={200}>
                        <AutocompleteWrapper
                          placeholder="Plan de monitoreo"
                          value={{ label: selectedMonitoringPlan?.label, value: selectedMonitoringPlan?.value } || { label: '', value: '' }}
                          options={monitoringPlanOptions}
                          onChange={(_, option) => setSelectedMonitoringPlan(option)}
                          size="very-small"
                          getOptionSelected={(option, value) => (option.value === value.value || value.value === '')}
                          clearable
                          fullWidth
                          fixEmptyWarn
                        />
                      </Box> */}
                      <Box display="flex" className={classes.group} p={0.5} ml={1}>
                        <SpeciesTypeFilter speciesType={eeccCondensedTypeFilter}
                          setSpeciesType={setEeccCondensedTypeFilter} showAllOption={true} />
                      </Box>
                    </>
                }
              >
                <Divider />
                {isLoadingEecc && <LoadingBox label="EECC agrupados" />}
                {(isSuccessEecc && !hasEecc) &&
                  <NoProcessBox label='No se encontraron especies EECC' />
                }
                <Box my={0.5} />
                {(hasEecc && isSuccessEecc) && <EeccCondensed eeccs={eeccs} eeccTypeFilter={eeccCondensedTypeFilter}/>}
              </AnalyticCard>
            </Grid>
          </Grid>
        }
        <Box my={0.5} />
        {(hasProtectedZones || isLoadingProtectedAndPriorityZones) &&
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <AnalyticCard title="Proximidad a áreas de importancia para la biodiversidad">
                <Divider />
                {isLoadingProtectedAndPriorityZones && <LoadingBox label="áreas protegidas" />}
                {(isSuccessProtectedAndPriorityZones && hasProtectedZones) &&
                  <Box mt={1}>
                    <ProtectedAndPriorityZones protectedAndPriorityZones={protectedAndPriorityZones} interestArea={interestArea.data} />
                  </Box>
                }
              </AnalyticCard>
            </Grid>
          </Grid>
        }
        <Box my={0.5} />
        {/* <Grid container spacing={1}>
          <Grid item xs={12}>
            <MonitoringPlanAnalytics />
          </Grid>
        </Grid> */}
      </ContentContainer>
      <DialogWrap maxWidth={dialog.type === 'photo' ? 'sm' : 'lg'} fullWidth onClose={closeDialog} open={dialog.isOpen}>
        {getDialogType()}
      </DialogWrap>
    </Grid>
  );
};

BiodiversityContainer.propTypes = {
  interestArea: PropTypes.shape({
    data: PropTypes.array,
    fetchStatus: PropTypes.object,
  }).isRequired,
  project: PropTypes.shape({
    siteName: PropTypes.string,
    siteId: PropTypes.number,
    name: PropTypes.string,
    id: PropTypes.number,
  }),
};


export { BiodiversityContainer };
